import type { ArticleSummary } from '@rabbit/shared';
import { ExternalLink, Share2 } from 'lucide-react';
import { useState, type CSSProperties } from 'react';
import type { Copy } from '../i18n';
import { useGestures } from '../hooks/useGestures';

interface ArticleCardProps {
  article: ArticleSummary;
  previousArticle?: ArticleSummary | undefined;
  nextArticle?: ArticleSummary | undefined;
  exploreArticle?: ArticleSummary | undefined;
  copy: Copy;
  anchorTitle?: string | undefined;
  parentTitle?: string | undefined;
  depth: number;
  loading: boolean;
  onNext: () => void;
  onPrevious: () => void;
  onExplore: () => void;
  onBack: () => void;
  onShare: () => void;
  onOpen: () => void;
}

interface ArticleSurfaceProps {
  article: ArticleSummary;
  copy: Copy;
  anchorTitle?: string | undefined;
  depth: number;
  className: string;
  style?: CSSProperties;
  hidden?: boolean;
  onShare?: () => void;
  onOpen?: () => void;
}

function articleKey(article: ArticleSummary): string {
  return `${article.lang}-${article.id}-${article.title}`;
}

function ArticleSurface({ article, copy, anchorTitle, depth, className, style, hidden, onShare, onOpen }: ArticleSurfaceProps) {
  const [imageFailed, setImageFailed] = useState(false);
  const [imageReady, setImageReady] = useState(false);
  const interactive = Boolean(onShare && onOpen);

  return (
    <section className={className} style={style} aria-hidden={hidden}>
      <div className="hero" aria-label={article.displayTitle}>
        {article.thumbnailUrl && !imageFailed ? (
          <img
            className={imageReady ? 'ready' : ''}
            src={article.originalImageUrl ?? article.thumbnailUrl}
            alt={`Wikipedia: ${article.displayTitle}`}
            draggable={false}
            onLoad={() => setImageReady(true)}
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="image-fallback" aria-hidden="true"><span>{article.displayTitle.slice(0, 1)}</span></div>
        )}
        <div className="hero-gradient" />
        <span className="article-count">{depth ? `${depth} · ${anchorTitle}` : copy.discover}</span>
      </div>
      <div className="article-copy">
        <div className="eyebrow">{depth ? `${copy.exploring}: ${anchorTitle}` : copy.discover}</div>
        <h1>{article.displayTitle}</h1>
        {article.description && <p className="description">{article.description}</p>}
        <p className="extract">{article.extract}</p>
        <div className="attribution"><span className="wiki-mark">W</span><span>{copy.wikipedia}</span></div>
        {interactive && (
          <div className="article-actions">
            <button type="button" className="text-action" onClick={onShare}><Share2 size={17} />{copy.shareArticle}</button>
            <button type="button" className="text-action" onClick={onOpen}>{copy.openWikipedia}<ExternalLink size={16} /></button>
          </div>
        )}
      </div>
    </section>
  );
}

export function ArticleCard(props: ArticleCardProps) {
  const gestures = useGestures({ disabled: props.loading, onDown: props.onPrevious, onUp: props.onNext, onLeft: props.onExplore, onRight: props.onBack });
  const drag = gestures.drag;
  const verticalDrag = drag.direction === 'up' || drag.direction === 'down' ? drag.y : 0;
  const horizontalDrag = drag.direction === 'left' || drag.direction === 'right' ? drag.x : 0;
  const verticalProgress = Math.min(Math.abs(verticalDrag) / 180, 1);
  const horizontalProgress = Math.min(Math.abs(horizontalDrag) / 170, 1);
  const tilt = Math.max(-2.4, Math.min(2.4, horizontalDrag / 110));
  const currentScale = drag.direction === 'up' || drag.direction === 'down' ? 1 - verticalProgress * 0.018 : 1;

  const currentTransform = horizontalDrag
    ? `translate3d(${horizontalDrag}px, 0, 0) rotate(${tilt}deg)`
    : `translate3d(0, ${verticalDrag}px, 0) scale(${currentScale})`;
  const previousTransform = `translate3d(0, calc(-100% + ${verticalDrag}px), 0) scale(${0.985 + verticalProgress * 0.015})`;
  const nextTransform = `translate3d(0, calc(100% + ${verticalDrag}px), 0) scale(${0.985 + verticalProgress * 0.015})`;
  const exploreTransform = `translate3d(calc(100% - ${Math.max(-horizontalDrag, 0)}px), 0, 0)`;
  const actionLabel = drag.direction === 'left'
    ? `${props.copy.explore} ${props.article.displayTitle}`
    : drag.direction === 'right' && props.depth > 0
      ? `${props.copy.backTo} ${props.parentTitle ?? props.copy.discover}`
      : '';
  const hint = drag.direction === 'down'
    ? props.copy.previous
    : drag.direction === 'up'
      ? props.anchorTitle ? `${props.copy.moreAbout} ${props.anchorTitle}` : props.copy.moreDiscovery
      : actionLabel;

  return (
    <main
      className={`article-deck ${drag.ready ? 'gesture-ready' : ''} ${drag.direction ? `drag-${drag.direction}` : ''}`}
      {...gestures.bind}
      aria-live="polite"
      data-testid="article-card"
    >
      {drag.direction === 'left' && props.exploreArticle ? (
        <ArticleSurface
          key={`explore-${articleKey(props.exploreArticle)}`}
          article={props.exploreArticle}
          copy={props.copy}
          anchorTitle={props.article.displayTitle}
          depth={props.depth + 1}
          className="article-panel topic-preview right"
          style={{ transform: exploreTransform }}
          hidden
        />
      ) : (
        <div className={`swipe-action-layer ${drag.direction === 'right' ? 'left' : 'right'}`} style={{ opacity: horizontalProgress }}>
          {actionLabel || (drag.direction === 'left' ? props.copy.loading : '')}
        </div>
      )}
      {props.previousArticle && (
        <ArticleSurface
          key={`previous-${articleKey(props.previousArticle)}`}
          article={props.previousArticle}
          copy={props.copy}
          anchorTitle={props.anchorTitle}
          depth={props.depth}
          className="article-panel article-panel-preview previous"
          style={{ transform: previousTransform, opacity: drag.direction === 'down' ? 1 : 0.35 }}
          hidden
        />
      )}
      {props.nextArticle && (
        <ArticleSurface
          key={`next-${articleKey(props.nextArticle)}`}
          article={props.nextArticle}
          copy={props.copy}
          anchorTitle={props.anchorTitle}
          depth={props.depth}
          className="article-panel article-panel-preview next"
          style={{ transform: nextTransform, opacity: drag.direction === 'up' ? 1 : 0.35 }}
          hidden
        />
      )}
      <ArticleSurface
        key={`current-${articleKey(props.article)}`}
        article={props.article}
        copy={props.copy}
        anchorTitle={props.anchorTitle}
        depth={props.depth}
        className="article-panel current"
        style={{ transform: currentTransform }}
        onShare={props.onShare}
        onOpen={props.onOpen}
      />
      {hint && <div className={`gesture-hint ${drag.direction ?? ''}`} role="status">{hint}</div>}
      {props.loading && <div className="topic-loading"><span />{props.copy.loading}</div>}
    </main>
  );
}
